import { useEffect, useState } from 'react';
import { ExportError, MAX_EXPORT_SECONDS, checkExportable } from '../../audio/render';
import { useObservable } from '../../lib/useObservable';
import { songDurationSeconds } from '../../music/compile';
import { useProject } from '../../state/projectContext';
import { useServices } from '../../state/services';
import { Alert, Button, Panel, Spinner } from '../ui';

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;

export function ExportPanel() {
  const { project, session } = useProject();
  const { exporter } = useServices();
  const state = useObservable(exporter.state);
  const [now, setNow] = useState(() => Date.now());
  const rendering = state.status === 'rendering';

  useEffect(() => {
    if (!rendering) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [rendering]);

  let blocker: string | null = null;
  try {
    checkExportable(project);
  } catch (error) {
    blocker = error instanceof ExportError ? error.message : 'This project cannot be exported.';
  }
  const seconds = songDurationSeconds(project);

  return (
    <Panel title="Export WAV">
      <p className="text-xs text-zinc-400">
        Renders the arrangement offline (not in real time) through the same instruments, mixer and effects as playback: 44.1 kHz, 16-bit stereo, up to{' '}
        {fmt(MAX_EXPORT_SECONDS)}. Song length: <span className="text-zinc-200">{fmt(seconds)}</span> + 2 s release tail.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          disabled={rendering || blocker !== null}
          onClick={() => {
            void exporter.export(session.snapshot.project ?? project).catch(() => undefined);
          }}
          data-testid="export-wav"
        >
          {rendering ? 'Rendering…' : 'Export WAV'}
        </Button>
        {state.status === 'done' && (
          <Button size="sm" onClick={() => exporter.downloadAgain()}>
            Download again
          </Button>
        )}
      </div>
      <div aria-live="polite" className="mt-3" data-testid="export-status" data-status={state.status}>
        {blocker && !rendering && <Alert tone="warning" title="Export unavailable">{blocker}</Alert>}
        {rendering && <Spinner label={`Rendering ${fmt(seconds)} of audio… ${Math.max(0, Math.round((now - (state.startedAt ?? now)) / 1000))} s elapsed`} />}
        {state.status === 'done' && <Alert tone="success" title="WAV ready">{state.summary}</Alert>}
        {state.status === 'error' && <Alert title="Export failed">{state.error}</Alert>}
      </div>
    </Panel>
  );
}
